import { useGrowl } from "PFApp/use_growl";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import DataTable from "PFComponents/data_table/data_table";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { saveThresholds } from "../../services/api";
import { addExtraInfo } from "../booking_thresholds";
import { ThresholdsItem } from "../types";
import ColorBadge from "./color_badge";
import RowActions from "./row_actions";
import Thresholds from "./thresholds";
import css from "./thresholds_table.module.scss";

interface ThresholdsTableProps {
  data: ThresholdsItem[];
  kind: string;
  onColorEdit: (item: ThresholdsItem) => void;
  setData(prev: any): void;
  editedRow: ThresholdsItem | null;
  setEditedRow: React.Dispatch<React.SetStateAction<ThresholdsItem | null>>;
  onConfigUpdate?: () => void;
}

const ThresholdsTable = ({
  data,
  onColorEdit,
  setData,
  kind,
  setEditedRow,
  editedRow,
  onConfigUpdate
}: ThresholdsTableProps) => {
  const growl = useGrowl();
  const { t } = useTranslation("bookingModule");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [keyToRemove, setKeyToRemove] = useState<any>(null);
  const [erroKey, setErrorKey] = useState(null);
  const growlErrors = useErrorsGrowl();

  const submitThreshold = (saveData) => {
    const editedData = [
      ...data.filter((item) => item.key !== saveData.key),
      {
        ...saveData,
        color: editedRow?.color || saveData.color,
        min: Number(editedRow?.min || saveData.min),
        max: Number(editedRow?.max || saveData.max)
      }
    ];

    saveThresholds({
      kind: saveData.kind,
      thresholds: editedData.map(({ color, min, max }) => ({
        color,
        min: min,
        max: max
      }))
    })
      .then((res) => {
        setData(
          addExtraInfo(
            res.sort((thresholdA, thresholdB) => thresholdA.min - thresholdB.min),
            saveData.kind
          )
        );
        setEditedRow(null);
        setErrorKey(null);
        onConfigUpdate?.();
        growl({
          message: t("config.thresholds.thresholdsUpdateSuccess"),
          kind: "success"
        });
      })
      .catch(({ response }) => {
        setErrorKey(saveData.key);
        growlErrors(response);
      });
  };

  const handleRemoveItem = () => {
    const payload = {
      kind: kind,
      thresholds: data
        .filter((res) => res.key !== keyToRemove)
        .map((item) => ({
          color: item.color,
          min: item.min,
          max: item.max
        }))
    };
    // we use the same funciton to add and remove thresholds
    saveThresholds(payload)
      .then((res) => {
        setData(addExtraInfo(res, kind));
        setEditedRow(null);
        growl({
          message: t("config.thresholds.thresholdsDeleteSuccess"),
          kind: "success"
        });
      })
      .catch(({ response }) => growlErrors(response));
  };

  const isEditMode = (cellData) => editedRow?.key === cellData.key;

  return (
    <>
      <div className={css.table}>
        <DataTable
          columns={[
            {
              headerName: t("config.thresholds.triggerWhen"),
              // eslint-disable-next-line react/display-name
              renderCell: (cellData) => (
                <Thresholds
                  threshold={editedRow?.key === cellData.key ? { ...cellData } : cellData}
                  setThresholds={setEditedRow}
                  isEdit={isEditMode(cellData)}
                  errorKey={erroKey}
                />
              ),
              tdStyle: { width: 300, maxWidth: 200 }
            },
            {
              headerName: t("config.thresholds.color"),
              // eslint-disable-next-line react/display-name
              renderCell: (cellData) => (
                <ColorBadge
                  color={cellData.color}
                  editMode={isEditMode(cellData)}
                  onColorEdit={() => onColorEdit(cellData)}
                  editedColor={cellData.key === editedRow?.key ? editedRow?.color : null}
                />
              ),
              tdStyle: { width: 200, maxWidth: 200 }
            },
            {
              // eslint-disable-next-line react/display-name
              renderCell: (cellData) => (
                <RowActions
                  cellData={cellData}
                  setKeyToRemove={setKeyToRemove}
                  setConfirmRemove={setConfirmRemove}
                  setEditedRow={setEditedRow}
                  isEditMode={isEditMode(cellData)}
                  setData={setData}
                  submitThreshold={submitThreshold}
                  setErrorKey={setErrorKey}
                />
              ),
              narrow: true,
              tdStyle: { width: 200, maxWidth: 200 }
            }
          ]}
          data={data}
        />
      </div>
      {confirmRemove && (
        <ConfirmModal
          handleOK={handleRemoveItem}
          handleClose={() => {
            setConfirmRemove(false);
            setKeyToRemove(null);
          }}
        >
          {t("config.thresholds.confirmRemove")}
        </ConfirmModal>
      )}
    </>
  );
};

export default ThresholdsTable;
